import { c as _c } from "react/compiler-runtime";
import type { VaultValue } from '@onefootprint/core';
import { IcoUsers16 } from '@onefootprint/icons';
import type { DataIdentifier, Entity, SameTenantDupe } from '@onefootprint/request-types/dashboard';
import { Divider, Drawer, IconButton, LinkButton, Tooltip } from '@onefootprint/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FieldOrPlaceholder from '@/components/field-or-placeholder';
import { getBankDis } from '@/entities/utils/get-dis';
import useEntityDuplicateData from '@/entity/hooks/use-entity-duplicate-data';
import type { EntityBankAccount } from '@/types/vault';
import getBankAccounts from '../../../../../../utils/get-bank-accounts';
import type { DiField } from '../../../../../../vault.types';
import Field from '../../../../../field';
import useGetTranslationWithoutAlias from '../../utils/use-get-translation-without-alias';
import AccountBalances from './account-balances';
import AccountStatus from './account-status';
import BankDuplicateDrawer from './bank-duplicate-drawer/bank-duplicate-drawer';
import BankAccountSelector from './components/bank-account-selector';
import useLinkedBankAccounts from './hooks/use-linked-bank-accounts';
export type BankAccountFieldsProps = {
  entity: Entity;
  setSelectedItemDis: (dis: DataIdentifier[]) => void;
};
enum BankFieldCategory {
  LINK = 'mk_link_id',
  INSTITUTION_ID = 'institution_id',
  INSTITUTION_NAME = 'institution_name',
  ACCOUNT_TYPE = 'account_type',
  ACCOUNT_NAME = 'name',
  ACCOUNT_NUMBER = 'account_last4',
  ACH_ACCOUNT = 'ach_account_number',
  ACH_ROUTING = 'ach_routing_number',
  OWNERS = 'owners',
  FINGERPRINT = 'fingerprint',
}
const FIELD_ORDER = Object.values(BankFieldCategory);
const getSortOrder = (di: string) => {
  const field = di.split('.').pop() ?? '';
  const index = FIELD_ORDER.indexOf(field as BankFieldCategory);
  return index === -1 ? FIELD_ORDER.length : index;
};
const BankAccountFields = t0 => {
  const $ = _c(54);
  const {
    entity,
    setSelectedItemDis
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset.financial.control"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  let t2;
  if ($[1] !== entity) {
    t2 = getBankAccounts(entity);
    $[1] = entity;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const bankAccounts = t2;
  const [selectedBankAccount, setSelectedBankAccount] = useState(bankAccounts.length > 0 ? bankAccounts[0] : undefined);
  const {
    data: linkedBankAccounts
  } = useLinkedBankAccounts(entity.id);
  const [duplicateDrawerOpen, setDuplicateDrawerOpen] = useState(false);
  const [ownersDrawerOpen, setOwnersDrawerOpen] = useState(false);
  const getTranslationsWithoutAlias = useGetTranslationWithoutAlias();
  const {
    data: duplicateData,
    isLoading: isDuplicateDataLoading
  } = useEntityDuplicateData(entity.id);
  let t3;
  if ($[3] !== duplicateData?.data || $[4] !== selectedBankAccount?.fingerprint) {
    t3 = duplicateData?.data.filter(dupe => dupe.data.some(data => data.value === selectedBankAccount?.fingerprint));
    $[3] = duplicateData?.data;
    $[4] = selectedBankAccount?.fingerprint;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const filteredDupes = t3;
  const hasDupes = !!filteredDupes?.length;
  const allBankDis = getBankDis(entity.data, "");
  const t4 = selectedBankAccount?.alias;
  let t5;
  if ($[6] !== entity.data || $[7] !== t4) {
    t5 = getBankDis(entity.data, t4);
    $[6] = entity.data;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const selectedBankDis = t5;
  const fields = allBankDis.map(_temp).sort(_temp2);
  let t6;
  if ($[9] !== selectedBankDis || $[10] !== setSelectedItemDis) {
    t6 = () => {
      setSelectedItemDis(selectedBankDis);
    };
    $[9] = selectedBankDis;
    $[10] = setSelectedItemDis;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== selectedBankAccount) {
    t7 = [selectedBankAccount];
    $[12] = selectedBankAccount;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  useEffect(t6, t7);
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      setDuplicateDrawerOpen(false);
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const handleDuplicationDrawerClose = t8;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setDuplicateDrawerOpen(true);
    };
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const handleDuplicationDrawerOpen = t9;
  let t10;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = () => {
      setOwnersDrawerOpen(false);
    };
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const handleOwnersDrawerClose = t10;
  let t11;
  if ($[17] !== t) {
    t11 = value => {
      if (!value) {
        return <p className="text-body-2 text-tertiary">{t(_temp3)}</p>;
      }
      if (typeof value === "object" && value !== null) {
        return <pre className="text-snippet-3 text-tertiary" data-dd-privacy="mask">{JSON.stringify(value, null, 2)}</pre>;
      }
      try {
        const owners = JSON.parse(value as string);
        return <pre className="text-snippet-3 text-tertiary" data-dd-privacy="mask">{JSON.stringify(owners, null, 2)}</pre>;
      } catch {
        return <p className="text-body-2 text-tertiary">{t(_temp4)}</p>;
      }
    };
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const renderJson = t11;
  let t12;
  if ($[19] !== hasDupes || $[20] !== t) {
    t12 = value_0 => {
      if (typeof value_0 === "string") {
        return <div className="flex gap-1 items-center"><FieldOrPlaceholder data={value_0} />{hasDupes && <Tooltip content={t(_temp5)}><IconButton size="compact" aria-label={t(_temp6)} icon={IcoUsers16} onClick={handleDuplicationDrawerOpen} /></Tooltip>}</div>;
      }
      return null;
    };
    $[19] = hasDupes;
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  const renderBankAccountFingerprint = t12;
  let t13;
  if ($[22] !== ownersDrawerOpen || $[23] !== renderJson || $[24] !== t) {
    t13 = value_1 => <><LinkButton onClick={() => setOwnersDrawerOpen(true)}>See details</LinkButton><Drawer open={ownersDrawerOpen} onClose={handleOwnersDrawerClose} title={t(_temp7)}>{renderJson(value_1)}</Drawer></>;
    $[22] = ownersDrawerOpen;
    $[23] = renderJson;
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const renderOwnersLink = t13;
  let t14;
  if ($[26] !== entity || $[27] !== getTranslationsWithoutAlias || $[28] !== renderBankAccountFingerprint || $[29] !== renderOwnersLink || $[30] !== selectedBankDis) {
    t14 = field => {
      const {
        di: di_0
      } = field;
      if (!selectedBankDis.includes(di_0)) {
        return <div className="hidden"><Field key={di_0} di={di_0} entity={entity} /></div>;
      }
      const checkEnd = suffix => {
        const parts = di_0.split(".");
        return parts[parts.length - 1] === suffix;
      };
      switch (true) {
        case checkEnd("fingerprint"):
          {
            return <Field renderValue={renderBankAccountFingerprint} key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
          }
        case checkEnd("institution_id") || checkEnd("link_id") || checkEnd("ach_account_number") || checkEnd("ach_routing_number"):
          {
            return <Field key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} monospaced={true} />;
          }
        case checkEnd("owners"):
          {
            return <Field key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} renderValue={renderOwnersLink} />;
          }
        default:
          {
            return <Field key={di_0} di={di_0} entity={entity} renderLabel={() => getTranslationsWithoutAlias(di_0)} />;
          }
      }
    };
    $[26] = entity;
    $[27] = getTranslationsWithoutAlias;
    $[28] = renderBankAccountFingerprint;
    $[29] = renderOwnersLink;
    $[30] = selectedBankDis;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  const renderField = t14;
  const t15 = "flex flex-col gap-3";
  let t16;
  if ($[32] !== bankAccounts || $[33] !== selectedBankAccount) {
    t16 = bankAccounts.length > 1 && <><BankAccountSelector bankAccounts={bankAccounts} selected={selectedBankAccount} onChange={setSelectedBankAccount} /><Divider /></>;
    $[32] = bankAccounts;
    $[33] = selectedBankAccount;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  const t17 = "flex flex-col gap-3";
  let t18;
  if ($[35] !== renderField) {
    t18 = field_0 => renderField(field_0);
    $[35] = renderField;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  const t19 = fields.map(t18);
  let t20;
  if ($[37] !== t19) {
    t20 = <div className={t17}>{t19}</div>;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== linkedBankAccounts || $[40] !== selectedBankAccount) {
    t21 = linkedBankAccounts && <div className="flex flex-col gap-2 mt-1"><AccountStatus links={linkedBankAccounts.links} selectedBankAccount={selectedBankAccount} /><AccountBalances accounts={linkedBankAccounts.accounts} selectedBankAccount={selectedBankAccount} /></div>;
    $[39] = linkedBankAccounts;
    $[40] = selectedBankAccount;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[42] !== filteredDupes) {
    t22 = filteredDupes ?? [];
    $[42] = filteredDupes;
    $[43] = t22;
  } else {
    t22 = $[43];
  }
  let t23;
  if ($[44] !== duplicateDrawerOpen || $[45] !== handleDuplicationDrawerClose || $[46] !== isDuplicateDataLoading || $[47] !== t22) {
    t23 = <BankDuplicateDrawer isOpen={duplicateDrawerOpen} onClose={handleDuplicationDrawerClose} dupes={t22} isLoading={isDuplicateDataLoading} />;
    $[44] = duplicateDrawerOpen;
    $[45] = handleDuplicationDrawerClose;
    $[46] = isDuplicateDataLoading;
    $[47] = t22;
    $[48] = t23;
  } else {
    t23 = $[48];
  }
  let t24;
  if ($[49] !== t16 || $[50] !== t20 || $[51] !== t21 || $[52] !== t23) {
    t24 = <div className={t15}>{t16}{t20}{t21}{t23}</div>;
    $[49] = t16;
    $[50] = t20;
    $[51] = t21;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  return t24;
};
export default BankAccountFields;
function _temp(di) {
  return {
    di
  };
}
function _temp2(a, b) {
  return getSortOrder(a.di) - getSortOrder(b.di);
}
function _temp3($) {
  return $.noOwner;
}
function _temp4($_0) {
  return $_0.invalidOwnerData;
}
function _temp5($_1) {
  return $_1.cta;
}
function _temp6($_2) {
  return $_2.cta;
}
function _temp7($_3) {
  return $_3.owners;
}